import type { FormApi } from '../../form-hooks/useFormApi';
import type { SpaceAdminFormSubmitFn } from '../../form/SpaceAdminForm/SpaceAdminForm';

import { use, useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import moment from 'moment';

import { putSpaceHasPlan } from '../../lib/flotiq-client';
import {
  parseResponseErrors,
  ResponseError,
} from '../../lib/flotiq-client/response-errors';
import { getTestProps } from '../../lib/helpers';

import Button from '../../components/Button/Button';
import ContentObjectInformation from '../../components/ContentObjectInformation/ContentObjectInformation';
import { useGridNavigate } from '../../components/DataGrid/useGridFilters';
import Dropdown from '../../components/Dropdown/Dropdown';
import Heading from '../../components/Heading/Heading';
import LinkButton from '../../components/LinkButton/LinkButton';
import Loader from '../../components/Loader/Loader';
import PlanHistoryModalContent from '../../components/PlanHistoryModalContent/PlanHistoryModalContent';
import TopbarBreadcrumbs from '../../components/Topbar/breadcrumbs/TopbarBreadcrumbs';
import TopbarActionMenu from '../../components/Topbar/buttons/base/TopbarActionMenu';
import TopbarCancelButton from '../../components/Topbar/buttons/TopbarCancelButton';
import TopbarDeleteButton from '../../components/Topbar/buttons/TopbarDeleteButton';
import TopbarSaveAndLeaveButton from '../../components/Topbar/buttons/TopbarSaveAndLeaveButton';
import TopbarSaveButton from '../../components/Topbar/buttons/TopbarSaveButton';

import { useAllPlans, useSpace } from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import useToken from '../../hooks/useToken';

import { useModals } from '../../contexts/ModalContext';
import UserContext from '../../contexts/UserContext';

import { WarningIcon } from '../../images/shapes';

import SpaceAdminForm from '../../form/SpaceAdminForm/SpaceAdminForm';
import PageLayout, {
  predefinedLayoutClasses,
} from '../../layout/PageLayout/PageLayout';

const testId = 'add-space';

const AddSpace = () => {
  const jwt = useToken();
  const { t } = useTranslation();
  const modal = useModals();
  const { isRoleAdmin } = use(UserContext);
  const navigateOnSave = useRef<boolean>(false);

  const { navigateGrid, gridLink } = useGridNavigate(
    'spaces',
    '/spaces-data-preview',
  );

  const { id: spaceId } = useParams();

  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [planId, setPlanId] = useState('');

  const [formState, setFormState] = useState<FormApi>();

  const spaceOptions = useMemo(
    () => ({
      pause: !isRoleAdmin || !spaceId,
    }),
    [isRoleAdmin, spaceId],
  );

  const {
    entity: space,
    isLoading: spaceIsLoading,
    updateEntity: updateSpace,
    deleteEntity: deleteSpace,
    status: spaceStatus,
    errors: spaceErrors,
  } = useSpace(spaceId, null, spaceOptions);

  useApiErrorsToast(spaceErrors);

  const plansParams = useMemo(
    () => ({
      page: 1,
      limit: 1000,
    }),
    [],
  );

  const allPlansOptions = useMemo(
    () => ({
      pause: !isRoleAdmin,
    }),
    [isRoleAdmin],
  );

  const { data: allPlans, errors: allPlansErrors } = useAllPlans(
    plansParams,
    allPlansOptions,
  );

  useApiErrorsToast(allPlansErrors);

  const handleUpdate = useCallback<SpaceAdminFormSubmitFn>(
    async (data) => {
      setIsSaving(true);
      try {
        if (planId && space?.planLimits?.id !== planId) {
          const {
            body: planChangeBody,
            status: planChangeStatus,
            ok: planChangeOk,
          } = await putSpaceHasPlan(jwt, space?.id, {
            id: space?.id,
            planId: planId,
          });
          if (!planChangeOk) {
            throw parseResponseErrors(planChangeBody, planChangeStatus);
          }
          toast.success(t('Spaces.Form.PlanUpdated'));
        }
        const { body, status, ok } = await updateSpace({
          ...data,
          paymentDueDate: data.paymentDueDate
            ? moment(data.paymentDueDate).toISOString()
            : null,
        });

        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        toast.success(t('Spaces.Form.Updated'));
        setIsSaving(false);
        return [[data, {}], false];
      } catch (error) {
        setIsSaving(false);

        if (!(error instanceof ResponseError)) {
          toast.error(t('Form.CommunicationErrorMessage'));
          return [[data, {}], true];
        }

        if (!error.errors || !Object.keys(error.errors).length) {
          toast.error(t('Spaces.Form.CouldntUpdate'));
          return [[data, {}], true];
        }

        return [
          [
            data,
            typeof error.errors === 'string'
              ? { '': error.errors }
              : error.errors,
          ],
          true,
        ];
      }
    },
    [jwt, planId, space, t, updateSpace],
  );

  const handleDeleteSpace = useCallback(async () => {
    modal.deleting('delete-modal');
    try {
      const { body, status, ok } = await deleteSpace();
      if (!ok) {
        throw parseResponseErrors(body, status);
      }
      toast.success(t('Spaces.Form.Deleted'));
      navigateGrid();
    } catch (error) {
      if (!(error instanceof ResponseError)) {
        toast.error(t('Form.CommunicationErrorMessage'));
        return;
      }
      toast.error(t('Spaces.Form.CouldntDelete'));
    }
  }, [deleteSpace, modal, navigateGrid, t]);

  const handleDelete = useCallback(async () => {
    setIsDeleting(true);
    await modal.delete(t('Spaces.ConfirmDelete'), 'delete-modal', () =>
      handleDeleteSpace(),
    );
    setIsDeleting(false);
  }, [handleDeleteSpace, modal, t]);

  const isFormDisabled = isSaving || isDeleting;

  const showForm = (!spaceIsLoading && space) || !spaceId;

  const noAccess = useMemo(() => {
    return (
      <Heading
        level={2}
        additionalClasses="text-3xl md:text-4xl leading-8 dark:text-slate-200"
      >
        <div
          className="flex flex-col items-center justify-center text-center"
          {...getTestProps(testId, 'empty-data')}
        >
          <WarningIcon className="text-red w-14 md:w-20 mb-3" />
          {t('Global.NoAccess')}
        </div>
      </Heading>
    );
  }, [t]);

  const emptyResult = useMemo(() => {
    if (!isRoleAdmin) {
      return noAccess;
    }

    if (showForm) return null;
    if (spaceId && spaceIsLoading) {
      return (
        <Loader
          size="small"
          type="spinner-grid"
          {...getTestProps(testId, 'loader', 'testId')}
        />
      );
    }

    return (
      <Heading
        level={2}
        additionalClasses="text-3xl md:text-4xl leading-8 dark:text-slate-200"
      >
        <div
          className="flex flex-col items-center justify-center text-center"
          {...getTestProps(testId, 'empty-data')}
        >
          <WarningIcon
            className="text-red w-14 md:w-20 mb-3"
            title={t('Spaces.CouldntFind')}
          />
          {spaceStatus === 404
            ? t('Spaces.Form.CouldntFind', { spaceId: spaceId })
            : t('Form.CommunicationErrorMessage')}
        </div>
      </Heading>
    );
  }, [
    isRoleAdmin,
    noAccess,
    spaceId,
    spaceIsLoading,
    spaceStatus,
    showForm,
    t,
  ]);

  const openPlanHistoryModal = useCallback(async () => {
    await modal.info(
      t('PlanHistory.Title'),
      <PlanHistoryModalContent
        spaceId={spaceId}
        {...getTestProps(testId, 'plan-history', 'testId')}
      />,
      'plan-history',
      { size: 'lg' },
    );
  }, [modal, t, spaceId]);

  const allUsersFromSpaceLink = useMemo<string>(() => {
    const url = new URL('/users-data-preview', window.location.origin);
    url.searchParams.set(
      'filters',
      JSON.stringify({
        'space.id': {
          filter: space?.id,
        },
        spaces: {
          filter: space?.name,
        },
      }),
    );

    return url.toString();
  }, [space]);

  return (
    <PageLayout
      page="spaces-data-preview"
      title={
        !spaceId
          ? t('Spaces.AddSpace')
          : t('Spaces.EditSpace', { name: space?.name })
      }
      buttonsDisabled={isSaving || isDeleting}
      breadcrumbs={
        <TopbarBreadcrumbs
          parentTitle={t('Global.Spaces')}
          parentLink={gridLink}
        />
      }
      buttons={
        isRoleAdmin ? (
          <>
            <TopbarCancelButton isDirty={formState?.dirty} link={gridLink} />
            <TopbarSaveButton
              form="space-form"
              isLoading={isSaving}
              navigateOnSave={navigateOnSave}
            />
            <TopbarActionMenu>
              <TopbarSaveAndLeaveButton
                form="space-form"
                navigateOnSave={navigateOnSave}
              />
              {spaceId && <TopbarDeleteButton onClick={handleDelete} />}
            </TopbarActionMenu>
          </>
        ) : null
      }
      testId={testId}
    >
      {showForm && isRoleAdmin ? (
        <div className={predefinedLayoutClasses.withSidebar}>
          <div className={predefinedLayoutClasses.leftColumnWhite}>
            <SpaceAdminForm
              space={space}
              onSubmit={handleUpdate}
              disabled={isFormDisabled}
              navigateOnSave={navigateOnSave}
              setFormState={setFormState}
              testId={testId}
            />
            <div className="px-5 pb-8 md:px-12">
              <Heading level={3} additionalClasses="dark:text-slate-200">
                {t('Spaces.Plan')}
              </Heading>
              <Dropdown
                name="planId"
                value={space?.planLimits?.id}
                options={allPlans.map((plan) => ({
                  label: plan.name,
                  value: plan.id,
                }))}
                onChange={(event) => {
                  setPlanId(event.target.value);
                }}
              />
            </div>
          </div>
          <div className={predefinedLayoutClasses.rightColumn}>
            <div className="flex flex-col gap-4">
              {spaceId && (
                <Button
                  type="button"
                  onClick={openPlanHistoryModal}
                  {...getTestProps(testId, 'open-plan-history', 'testId')}
                >
                  {t('PlanHistory.View')}
                </Button>
              )}
              {spaceId && isRoleAdmin && (
                <LinkButton link={allUsersFromSpaceLink}>
                  {t('Global.UsersAssignedToSpace')}
                </LinkButton>
              )}
            </div>
            <ContentObjectInformation
              createdAt={space?.createdAt}
              updatedAt={space?.updatedAt}
              testId={testId}
            />
          </div>
        </div>
      ) : (
        <div className={predefinedLayoutClasses.whiteBox}>{emptyResult}</div>
      )}
    </PageLayout>
  );
};
export default AddSpace;
